import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import {
  Cog, AlertCircle, AlertTriangle, CheckCircle2, Info, Copy, RotateCcw, Pause, Play, XCircle,
} from 'lucide-react';
import {
  DEFAULT_INPUTS, FIELD_SPECS, GEAR_KEYS, THS_KEYS, LINK_KEYS, MEMBER_SPEED_KEY, MEMBERS, PRESETS,
  PRESET_NOTES, DRIVING_MODES, DRIVING_MODE_TEXT, STATE_LABELS, validateField, validateGearSet,
} from '../../lib/powerSplitTools';
import { powerSplitToolApi } from '../../lib/toolsApi';
import ArchitectureDiagram from './powerSplit/ArchitectureDiagram';
import PlanetaryCutaway from './powerSplit/PlanetaryCutaway';
import LeverDiagram from './powerSplit/LeverDiagram';
import SweepChart from './powerSplit/SweepChart';
import { fmtSigned, usePrefersReducedMotion } from './powerSplit/svgKit';

const CALC_DEBOUNCE_MS = 350;
const NUMERIC_KEYS = Object.keys(FIELD_SPECS);
const CHOICE_KEYS = ['unknownMember', 'torqueMember', 'speedBasis', 'mg2Mode', 'boostFitted'];
// Editing any of these after picking a preset turns the preset selector back to Custom.
const PRESET_KEYS = ['sunTeeth', 'ringTeeth', 'pinionTeeth', 'planetCount', 'mg2Ratio', 'finalDriveRatio', 'tyreRadiusM'];

const toRaw = (v) => (v === null || v === undefined ? '' : String(v));
const buildRaw = (values) => Object.fromEntries(NUMERIC_KEYS.map((k) => [k, toRaw(values[k])]));
const buildChoices = (values) => Object.fromEntries(CHOICE_KEYS.map((k) => [k, values[k]]));
const fmt = (n, d = 2) => fmtSigned(n, d);

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground truncate">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const ErrorText = ({ children, testId }) => (
  <p className="flex items-start gap-1 text-xs text-destructive" data-testid={testId}>
    <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" /> <span>{children}</span>
  </p>
);

const Section = ({ title, children, right }) => (
  <div className="space-y-4 p-4 bg-muted/50 rounded-md">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const PowerSplitCalculator = () => {
  const [tab, setTab] = useState('ths');
  const [raw, setRaw] = useState(() => buildRaw(DEFAULT_INPUTS));
  const [choices, setChoices] = useState(() => buildChoices(DEFAULT_INPUTS));
  const [preset, setPreset] = useState('custom');
  const [drivingMode, setDrivingMode] = useState(null);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const animate = !paused && !reducedMotion;

  const setField = (key, value) => {
    setRaw((prev) => ({ ...prev, [key]: value }));
    if (PRESET_KEYS.includes(key)) setPreset('custom');
  };
  const setChoice = (key, value) => setChoices((prev) => ({ ...prev, [key]: value }));
  const applyValues = (values) => {
    setRaw((prev) => ({ ...prev, ...buildRaw({ ...prev, ...values }) }));
    setChoices((prev) => ({ ...prev, ...Object.fromEntries(CHOICE_KEYS.filter((k) => k in values).map((k) => [k, values[k]])) }));
  };

  const handlePreset = (value) => {
    setPreset(value);
    if (PRESETS[value]) applyValues(PRESETS[value].values);
  };
  const handleDrivingMode = (key) => {
    setDrivingMode(key);
    applyValues(DRIVING_MODES[key].values);
  };
  const handleReset = () => {
    setRaw(buildRaw(DEFAULT_INPUTS));
    setChoices(buildChoices(DEFAULT_INPUTS));
    setPreset('custom');
    setDrivingMode(null);
  };

  const isGeneral = tab === 'general';

  // ---- Client-side validation (instant feedback; the backend re-validates) ----
  const { fieldErrors, payload } = useMemo(() => {
    const errors = {};
    const values = {};
    const check = (key, optional = false) => {
      const spec = { ...FIELD_SPECS[key], optional: optional || FIELD_SPECS[key].optional };
      const err = validateField(spec, raw[key]);
      if (err) errors[key] = err;
      else values[key] = raw[key] === '' ? null : Number(raw[key]);
    };
    GEAR_KEYS.forEach((k) => check(k));
    if (isGeneral) {
      MEMBERS.filter((m) => m.value !== choices.unknownMember).forEach((m) => check(MEMBER_SPEED_KEY[m.value]));
      check('torqueNm');
    } else {
      THS_KEYS.forEach((k) => check(k));
      const linkGiven = LINK_KEYS.some((k) => raw[k] !== '');
      if (choices.speedBasis === 'vehicle') {
        check('vehicleSpeedKmh');
        LINK_KEYS.forEach((k) => check(k));
      } else {
        check('ringRpm');
        if (linkGiven) LINK_KEYS.forEach((k) => check(k));
      }
      check(choices.mg2Mode === 'torque' ? 'mg2TorqueNm' : 'batteryTargetKw');
    }
    validateGearSet(values, errors);
    const body = { mode: isGeneral ? 'general' : 'ths', ...choices };
    NUMERIC_KEYS.forEach((k) => {
      body[k] = raw[k] === '' ? (k === 'pinionTeeth' ? null : '') : Number(raw[k]);
    });
    return { fieldErrors: errors, payload: body };
  }, [raw, choices, isGeneral]);

  const hasClientErrors = Object.keys(fieldErrors).length > 0;
  const payloadKey = JSON.stringify(payload);

  const [results, setResults] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasClientErrors) {
      setResults(null);
      setServerErrors({});
      setCalcError(null);
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      powerSplitToolApi.calculate(JSON.parse(payloadKey))
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setServerErrors(outcome.errors);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setServerErrors({});
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setServerErrors({});
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasClientErrors, payloadKey]);

  const errorFor = (key) => fieldErrors[key] || serverErrors[key];
  // Results of the other tab's mode are stale while the new request is in flight.
  const r = results && results.mode === (isGeneral ? 'general' : 'ths') ? results : null;

  const handleCopy = async () => {
    if (!r) return;
    const g = r.gear;
    const lines = [
      'Planetary Power-Split (eCVT) Calculator',
      `Gear set: Zs ${g.zs}, Zp ${g.zp}${g.zpAuto ? ' (standard)' : ''}, Zr ${g.zr}, N ${g.n}, mesh efficiency ${g.meshEffPct} %`,
      `Speeds: sun ${fmt(r.speeds.sunRpm, 0)} rpm, carrier ${fmt(r.speeds.carrierRpm, 0)} rpm, ring ${fmt(r.speeds.ringRpm, 0)} rpm`,
      `Pinion relative to carrier: ${fmt(r.pinion.relRpm, 0)} rpm`,
      ...r.rows.map((row) => `${row.name}: ${row.rpm === null ? '' : `${fmt(row.rpm, 0)} rpm, `}${row.torqueNm === null ? '' : `${fmt(row.torqueNm, 2)} N·m, `}${fmt(row.powerKw, 2)} kW (${row.state})`),
      ...(r.mode === 'ths'
        ? [
          `Operating state: ${r.operatingState.label}`,
          r.split ? `Engine power split: mechanical ${fmt(r.split.mechanicalPct, 1)} %, electrical ${fmt(r.split.electricalPct, 1)} %` : '',
          `Power balance: engine ${fmt(r.balance.engineKw, 2)} kW = ring ${fmt(r.balance.ringKw, 2)} kW + MG1 ${fmt(r.balance.mg1TakenKw, 2)} kW + mesh loss ${fmt(r.balance.meshLossKw, 2)} kW`,
        ]
        : [`Power balance: in ${fmt(r.balance.inKw, 2)} kW = out ${fmt(r.balance.outKw, 2)} kW + mesh loss ${fmt(r.balance.meshLossKw, 2)} kW`]),
      ...(r.warnings.length ? ['', 'Warnings:', ...r.warnings.map((w) => `  - ${w.message}`)] : []),
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const numField = (key, { tip, label, disabled, value, placeholder } = {}) => {
    const spec = FIELD_SPECS[key];
    const error = disabled ? null : errorFor(key);
    const id = `ps-${key}`;
    return (
      <div className="space-y-2 min-w-0" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{label || spec.label}</FieldLabel>
        <Input
          id={id} type="number" step="any" inputMode="decimal" value={value ?? raw[key]} disabled={disabled}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono" placeholder={placeholder}
          aria-invalid={!!error} data-testid={`${id}-input`}
        />
        {error && <ErrorText testId={`${id}-error`}>{error}</ErrorText>}
      </div>
    );
  };

  const standardZp = (() => {
    const zs = Number(raw.sunTeeth);
    const zr = Number(raw.ringTeeth);
    return Number.isFinite(zs) && Number.isFinite(zr) && zr > zs && (zr - zs) % 2 === 0 ? `auto: ${(zr - zs) / 2}` : 'auto';
  })();

  const animationToggle = (
    <Button
      type="button" variant="outline" size="sm" onClick={() => setPaused((p) => !p)} disabled={reducedMotion}
      data-testid="ps-animation-toggle" title={reducedMotion ? 'Animation is off because your system asks for reduced motion.' : undefined}
    >
      {reducedMotion ? <><Pause className="h-3.5 w-3.5" /> Reduced motion</>
        : paused ? <><Play className="h-3.5 w-3.5" /> Animate</> : <><Pause className="h-3.5 w-3.5" /> Pause</>}
    </Button>
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="technical-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="min-w-0">
                <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                  <Cog className="h-5 w-5 text-autoxium-blue shrink-0" />
                  Planetary Power-Split (eCVT) Calculator
                </CardTitle>
                <CardDescription>
                  See how one planetary gear set lets a power-split hybrid run the engine at any speed,
                  whatever the vehicle speed. Results update live as you edit.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!r} data-testid="ps-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleReset} data-testid="ps-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full sm:w-[34rem] h-auto">
            <TabsTrigger value="general" className="text-xs sm:text-sm whitespace-normal py-1.5" data-testid="ps-tab-general">General planetary</TabsTrigger>
            <TabsTrigger value="ths" className="text-xs sm:text-sm whitespace-normal py-1.5" data-testid="ps-tab-ths">Power-split (THS)</TabsTrigger>
            <TabsTrigger value="explorer" className="text-xs sm:text-sm whitespace-normal py-1.5" data-testid="ps-tab-explorer">Driving modes</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'explorer' && (
          <DrivingModePicker selected={drivingMode} detected={r?.operatingState} onPick={handleDrivingMode} />
        )}

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <Card className="technical-card lg:col-span-3 min-w-0">
            <CardContent className="pt-6 space-y-6">
              <Section
                title="1. Gear Set"
                right={(
                  <Select value={preset} onValueChange={handlePreset}>
                    <SelectTrigger className="w-full sm:w-72 h-8 text-xs" data-testid="ps-preset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                      {Object.entries(PRESETS).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              >
                {PRESET_NOTES[preset] && (
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="ps-preset-note">
                    {PRESET_NOTES[preset]} Approximate values from public sources.
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {numField('sunTeeth', { tip: 'Teeth on the sun gear (whole number).' })}
                  {numField('ringTeeth', { tip: 'Teeth on the internal ring gear. Must be more than the sun.' })}
                  {numField('pinionTeeth', { placeholder: standardZp, tip: 'Leave blank for the standard (Zr − Zs)/2. Enter a value for a profile-shifted set, e.g. 23 in the Prius 30/23/78. Only the pinion speed depends on it.' })}
                  {numField('planetCount', { tip: 'Equally spaced planets need (Zs + Zr)/N to be a whole number.' })}
                  {numField('meshEffPct', { tip: '100 % = lossless (default). Below 100 %, the loss is applied to the power flowing between sun and ring relative to the carrier, in whichever direction it flows.' })}
                  {numField('pinionMaxRpm', { tip: 'Warning limit for the pinion speed relative to the carrier (pinion bearings).' })}
                </div>
                {errorFor('gearSet') && <ErrorText testId="ps-gearSet-error">{errorFor('gearSet')}</ErrorText>}
              </Section>

              {isGeneral ? (
                <GeneralInputs
                  choices={choices} setChoice={setChoice} numField={numField} r={r}
                />
              ) : (
                <ThsInputs choices={choices} setChoice={setChoice} numField={numField} />
              )}
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4 min-w-0">
            {calcError ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <div className="flex items-start gap-2 text-sm text-destructive" data-testid="ps-calc-error">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{calcError}</span>
                </div>
              </CardContent></Card>
            ) : hasClientErrors ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="ps-results-placeholder">
                  Fix the highlighted fields to see results.
                </p>
              </CardContent></Card>
            ) : !r ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="ps-results-loading">Calculating…</p>
              </CardContent></Card>
            ) : r.mode === 'ths' ? <ThsSummary r={r} /> : <GeneralSummary r={r} />}
          </div>
        </div>

        {r && !hasClientErrors && !calcError && (
          <>
            {r.mode === 'ths' && (
              <Card className="technical-card min-w-0" data-testid="ps-architecture-card">
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="font-chivo uppercase tracking-tight text-sm">Power-Split Hybrid Architecture</CardTitle>
                      <CardDescription>
                        Engine on the carrier, MG1 on the sun, MG2 and the wheels on the ring. Live speed, torque and
                        power in every block; arrows follow the actual power flow.
                      </CardDescription>
                    </div>
                    {animationToggle}
                  </div>
                </CardHeader>
                <CardContent>
                  <ArchitectureDiagram r={r} animate={animate} />
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="technical-card min-w-0" data-testid="ps-cutaway-card">
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="font-chivo uppercase tracking-tight text-sm">Planetary Gear Set (to scale)</CardTitle>
                      <CardDescription>
                        All gears share one module, so sizes follow the tooth counts. Each member turns in proportion to
                        its speed (positive = clockwise here).
                      </CardDescription>
                    </div>
                    {r.mode === 'general' && animationToggle}
                  </div>
                </CardHeader>
                <CardContent>
                  <PlanetaryCutaway
                    gear={r.gear} speeds={r.speeds} pinionRelRpm={r.pinion.relRpm}
                    animate={animate} generalLabels={r.mode === 'general'}
                  />
                </CardContent>
              </Card>
              <Card className="technical-card min-w-0" data-testid="ps-lever-card">
                <CardHeader>
                  <CardTitle className="font-chivo uppercase tracking-tight text-sm">Lever (Nomograph) Diagram</CardTitle>
                  <CardDescription>Speed of each member on its own vertical axis; arrows show member torque.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <LeverDiagram lever={r.lever} gear={r.gear} generalLabels={r.mode === 'general'} />
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="ps-lever-explainer">
                    The Willis equation Zs·ωs + Zr·ωr = (Zs + Zr)·ωc is a weighted average, so the three speeds
                    always lie on one straight lever: fix any two
                    {r.mode === 'ths' ? ' (engine speed and vehicle speed)' : ''} and the third
                    {r.mode === 'ths' ? ' (MG1)' : ''} follows.
                  </p>
                </CardContent>
              </Card>
            </div>

            {r.mode === 'ths' && (
              <Card className="technical-card min-w-0">
                <CardHeader>
                  <CardTitle className="font-chivo uppercase tracking-tight text-sm">
                    MG1 &amp; MG2 Speed vs {r.sweep.xKey === 'speedKmh' ? 'Vehicle Speed' : 'Ring Speed'}
                  </CardTitle>
                  <CardDescription>
                    With the engine speed held constant, MG1 slows as the car speeds up, stops where
                    ring = (Zs + Zr)/Zr × engine speed, then turns backwards. The engine speed never has to follow
                    vehicle speed: MG1 absorbs the difference, within its speed limit.
                    {r.sweep.xKey !== 'speedKmh' && ' Enter the ring-to-axle ratio and tyre radius to plot against vehicle speed.'}
                  </CardDescription>
                </CardHeader>
                <CardContent><SweepChart sweep={r.sweep} /></CardContent>
              </Card>
            )}

            <ResultsTableCard r={r} />
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

// ---------------------------------------------------------------------------
// Inputs per tab
// ---------------------------------------------------------------------------

const ChoiceTabs = ({ value, onChange, options, testId }) => (
  <Tabs value={value} onValueChange={onChange}>
    <TabsList className="grid grid-cols-2 w-full sm:w-80 h-auto">
      {options.map((o) => (
        <TabsTrigger key={o.value} value={o.value} className="text-xs whitespace-normal py-1.5" data-testid={`${testId}-${o.value}`}>{o.label}</TabsTrigger>
      ))}
    </TabsList>
  </Tabs>
);

const GeneralInputs = ({ choices, setChoice, numField, r }) => {
  const solved = r && r.mode === 'general' ? r.speeds : null;
  return (
    <>
      <Section
        title="2. Speeds"
        right={(
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Solve for</span>
            <Select value={choices.unknownMember} onValueChange={(v) => setChoice('unknownMember', v)}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="ps-unknown-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MEMBERS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      >
        <p className="text-xs text-muted-foreground">Enter any two speeds; the third is solved from the Willis equation. Positive = one chosen direction of rotation.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {MEMBERS.map((m) => {
            const key = MEMBER_SPEED_KEY[m.value];
            const isUnknown = choices.unknownMember === m.value;
            return numField(key, {
              disabled: isUnknown,
              label: isUnknown ? `${FIELD_SPECS[key].label} (solved)` : undefined,
              value: isUnknown ? (solved ? String(solved[key]) : '') : undefined,
              placeholder: isUnknown ? 'solved' : undefined,
            });
          })}
        </div>
      </Section>
      <Section
        title="3. Known Torque"
        right={(
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Applied to</span>
            <Select value={choices.torqueMember} onValueChange={(v) => setChoice('torqueMember', v)}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="ps-torque-member-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MEMBERS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {numField('torqueNm', { tip: 'Torque applied to that member from outside (a shaft or machine), positive in the positive-speed direction. The other two torques follow from Ts : Tr : Tc = Zs : Zr : −(Zs + Zr).' })}
        </div>
      </Section>
    </>
  );
};

const ThsInputs = ({ choices, setChoice, numField }) => (
  <>
    <Section title="2. Operating Point">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {numField('engineRpm', { tip: 'Engine = planet carrier speed.' })}
        {numField('engineTorqueNm', { tip: 'Torque the engine applies to the carrier. Negative = the engine is being driven (e.g. cranked for a start).' })}
      </div>
      <div className="space-y-3">
        <ChoiceTabs
          value={choices.speedBasis} onChange={(v) => setChoice('speedBasis', v)} testId="ps-speed-basis"
          options={[{ value: 'ring', label: 'Ring speed' }, { value: 'vehicle', label: 'Vehicle speed' }]}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {choices.speedBasis === 'vehicle'
            ? numField('vehicleSpeedKmh', { tip: 'Negative = reversing. Needs the ring-to-axle ratio and tyre radius below.' })
            : numField('ringRpm', { label: 'Ring (Output) Speed', tip: 'Ring gear = output shaft speed, before the counter gear / final drive.' })}
        </div>
      </div>
      <div className="space-y-3">
        <ChoiceTabs
          value={choices.mg2Mode} onChange={(v) => setChoice('mg2Mode', v)} testId="ps-mg2-mode"
          options={[{ value: 'battery', label: 'Set battery power' }, { value: 'torque', label: 'Set MG2 torque' }]}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {choices.mg2Mode === 'torque'
            ? numField('mg2TorqueNm', { tip: 'Torque at the MG2 shaft. Positive at positive speed = motoring.' })
            : numField('batteryTargetKw', { tip: 'MG2 torque is solved so the battery delivers this power (positive = discharging). 0 = all of MG1\'s power goes to MG2.' })}
        </div>
      </div>
    </Section>
    <Section title="3. Driveline">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {numField('mg2Ratio', { tip: 'MG2 speed ÷ ring speed. 1 = MG2 directly on the ring (Prius Gen 2).' })}
        {numField('finalDriveRatio', { tip: 'Ring speed ÷ wheel speed (counter gear × final drive). Optional with the ring-speed basis.' })}
        {numField('tyreRadiusM', { tip: 'Loaded (dynamic) rolling radius. Optional with the ring-speed basis.' })}
      </div>
      <div className="flex items-center gap-3">
        <Switch
          id="ps-boost" checked={!!choices.boostFitted} onCheckedChange={(v) => setChoice('boostFitted', v)}
          data-testid="ps-boost-switch"
        />
        <Label htmlFor="ps-boost" className="text-xs">Boost converter fitted (diagram only; modelled as lossless)</Label>
      </div>
    </Section>
    <Accordion type="single" collapsible className="border border-structure rounded-md px-4 bg-muted/30">
      <AccordionItem value="limits" className="border-none">
        <AccordionTrigger className="hover:no-underline py-3" data-testid="ps-limits-toggle">
          <span className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">4. Efficiencies &amp; Limits</span>
        </AccordionTrigger>
        <AccordionContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            {numField('motorEffPct', { tip: 'Applied to both MG1 and MG2, in whichever direction power flows. 100 % = lossless.' })}
            {numField('inverterEffPct', { tip: 'Applied to both inverter legs. 100 % = lossless.' })}
            {numField('mg1MaxRpm', { tip: 'Warning when |MG1 speed| exceeds this. Also drawn on the sweep chart.' })}
            {numField('engineMaxRpm', { tip: 'Warning when the engine speed exceeds this.' })}
            {numField('sweepMaxKmh', { tip: 'Right end of the sweep chart.' })}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </>
);

// ---------------------------------------------------------------------------
// Driving-mode explorer
// ---------------------------------------------------------------------------

const DrivingModePicker = ({ selected, detected, onPick }) => {
  const text = selected ? DRIVING_MODE_TEXT[selected] : null;
  return (
    <Card className="technical-card" data-testid="ps-mode-picker">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Driving-Mode Explorer</CardTitle>
        <CardDescription>
          Pick a scenario: it fills the operating point (your gear set and limits stay) and the diagrams show the power
          flow. Edit anything afterwards to explore around it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {Object.entries(DRIVING_MODES).map(([k, m]) => {
            const isSel = selected === k;
            const isDetected = detected?.id === k;
            return (
              <button
                key={k} type="button" onClick={() => onPick(k)} aria-pressed={isSel}
                className={`relative rounded-md border px-2.5 py-2 text-left text-xs font-semibold leading-snug transition-colors ${isSel
                  ? 'border-autoxium-blue bg-autoxium-blue/10 text-foreground'
                  : 'border-structure bg-background hover:bg-muted'}`}
                data-testid={`ps-mode-${k}`}
              >
                {m.label}
                {isDetected && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-emerald-600" aria-label="current state" />}
              </button>
            );
          })}
        </div>
        {text && (
          <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1" data-testid="ps-mode-text">
            <p className="font-semibold">{DRIVING_MODES[selected].label}: {text.summary}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">What to watch: {text.watch}</p>
          </div>
        )}
        {detected && (
          <p className="text-xs text-muted-foreground" data-testid="ps-detected-state">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-600 mr-1.5" />
            Current inputs behave as: <span className="font-semibold text-foreground">{detected.label}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Summaries
// ---------------------------------------------------------------------------

const Stat = ({ label, value, sub, testId, tone }) => (
  <div className="min-w-0">
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className={`font-mono text-lg font-black break-words ${tone || 'text-autoxium-blue'}`} data-testid={testId}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const stateTone = (state) => (state === 'generating' || state === 'charging' ? 'text-emerald-700'
  : state === 'motoring' || state === 'discharging' ? 'text-autoxium-blue' : 'text-muted-foreground');

const WarningsNote = ({ r }) => {
  const n = r.warnings.filter((w) => w.level === 'warning').length;
  if (!n) return null;
  return (
    <p className="flex items-center gap-1.5 text-xs text-amber-700" data-testid="ps-warning-count">
      <AlertTriangle className="h-3.5 w-3.5" /> {n} warning{n > 1 ? 's' : ''}: see below the results table.
    </p>
  );
};

const SplitBar = ({ split }) => {
  if (!split) {
    return <p className="text-xs text-muted-foreground" data-testid="ps-split">The engine is not delivering power, so there is no power split.</p>;
  }
  const mech = Math.max(0, Math.min(100, split.mechanicalPct));
  const elec = Math.max(0, Math.min(100 - mech, split.electricalPct));
  return (
    <div className="space-y-1.5" data-testid="ps-split">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Engine power path</p>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        <div style={{ width: `${mech}%`, background: 'hsl(var(--foreground) / 0.75)' }} />
        <div style={{ width: `${elec}%`, background: 'hsl(var(--ps-elec))' }} />
      </div>
      <p className="text-xs">
        <span className="font-mono font-bold" data-testid="ps-split-mech">{fmt(split.mechanicalPct, 1)} %</span> mechanical (ring) ·{' '}
        <span className="font-mono font-bold" data-testid="ps-split-elec">{fmt(split.electricalPct, 1)} %</span> electrical (MG1 → MG2/battery)
      </p>
      {split.recirculating && (
        <p className="text-xs text-amber-700">Negative electrical share: MG1 is motoring, so power circulates (MG2 → MG1) instead of splitting.</p>
      )}
    </div>
  );
};

const ThsSummary = ({ r }) => (
  <div className="space-y-4" data-testid="ps-results">
    <Card className="technical-card ring-1 ring-autoxium-blue/25">
      <CardHeader className="pb-3">
        <Badge variant="outline" className="w-fit font-mono text-[11px]" data-testid="ps-operating-state">{r.operatingState.label}</Badge>
        <CardDescription className="text-xs leading-relaxed">{r.operatingState.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Stat label="MG1 speed" value={`${fmt(r.mg1.rpm, 0)} rpm`} testId="ps-mg1-rpm" />
          <Stat label="MG1 torque" value={`${fmt(r.mg1.torqueNm, 2)} N·m`} testId="ps-mg1-torque" />
          <Stat label="MG1 power" value={`${fmt(r.mg1.powerKw, 2)} kW`} sub={STATE_LABELS[r.mg1.state]} testId="ps-mg1-power" tone={stateTone(r.mg1.state)} />
          <Stat label="MG2 power" value={`${fmt(r.mg2.powerKw, 2)} kW`} sub={STATE_LABELS[r.mg2.state]} testId="ps-mg2-power" tone={stateTone(r.mg2.state)} />
          <Stat label="Battery" value={`${fmt(r.battery.powerKw, 2)} kW`} sub={STATE_LABELS[r.battery.state]} testId="ps-battery-power" tone={stateTone(r.battery.state)} />
          <Stat
            label={r.output.vehicleSpeedKmh === null ? 'Ring speed' : 'Vehicle speed'}
            value={r.output.vehicleSpeedKmh === null ? `${fmt(r.ring.rpm, 0)} rpm` : `${fmt(r.output.vehicleSpeedKmh, 1)} km/h`}
            testId="ps-vehicle-speed" tone="text-foreground"
          />
        </div>
        <SplitBar split={r.split} />
        <WarningsNote r={r} />
      </CardContent>
    </Card>
  </div>
);

const GeneralSummary = ({ r }) => {
  const u = r.unknownMember;
  const name = MEMBERS.find((m) => m.value === u).label;
  return (
    <div className="space-y-4" data-testid="ps-results">
      <Card className="technical-card ring-1 ring-autoxium-blue/25">
        <CardHeader className="pb-3">
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Solved</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Stat label={`${name} speed`} value={`${fmt(r.speeds[`${u}Rpm`], 1)} rpm`} testId="ps-solved-speed" />
          <div className="grid grid-cols-3 gap-3">
            {MEMBERS.map((m) => (
              <Stat key={m.value} label={`T ${m.value}`} value={`${fmt(r.torques[m.value], 2)}`} sub="N·m" testId={`ps-torque-${m.value}`} tone="text-foreground" />
            ))}
          </div>
          <Stat label="Pinion vs carrier" value={`${fmt(r.pinion.relRpm, 0)} rpm`} sub={`Zp ${r.gear.zp}${r.gear.zpAuto ? ' (standard)' : ''}`} testId="ps-pinion-rpm" tone="text-foreground" />
          <WarningsNote r={r} />
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Results table, power balance, warnings
// ---------------------------------------------------------------------------

const GENERAL_STATE = { driving: 'Input', driven: 'Output', idle: 'Idle' };

const BalanceLine = ({ ok, children, testId }) => (
  <p className="flex items-start gap-2 text-xs font-mono leading-relaxed" data-testid={testId}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-700" /> : <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-destructive" />}
    <span>{children}</span>
  </p>
);

const ResultsTableCard = ({ r }) => {
  const b = r.balance;
  const warnings = r.warnings.filter((w) => w.level === 'warning');
  const infos = r.warnings.filter((w) => w.level !== 'warning');
  return (
    <Card className="technical-card min-w-0" data-testid="ps-results-table-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Results by Member</CardTitle>
        <CardDescription>
          {r.mode === 'ths'
            ? 'Torque is what each machine applies to its shaft; power = torque × speed, positive = delivering (motoring). Ring torque is the torque it passes to the output.'
            : 'Torque is applied to each member from outside; power = torque × speed, positive = power into the gear set (input).'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table className="text-xs" data-testid="ps-results-table">
          <TableHeader>
            <TableRow className="text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent">
              <TableHead className="px-1.5 sm:px-2">Member</TableHead>
              <TableHead className="px-1.5 sm:px-2 text-right">rpm</TableHead>
              <TableHead className="px-1.5 sm:px-2 text-right">N·m</TableHead>
              <TableHead className="px-1.5 sm:px-2 text-right">kW</TableHead>
              <TableHead className="px-1.5 sm:px-2">State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {r.rows.map((row) => (
              <TableRow key={row.id} data-testid={`ps-row-${row.id}`}>
                <TableCell className="px-1.5 sm:px-2 py-2">
                  <span className="font-semibold">{row.name}</span>
                  {r.mode === 'ths' && <span className="block text-[10px] text-muted-foreground">{row.gearMember}</span>}
                  {r.mode === 'general' && row.solved && <span className="block text-[10px] text-autoxium-blue">solved</span>}
                </TableCell>
                <TableCell className="px-1.5 sm:px-2 py-2 text-right font-mono whitespace-nowrap" data-testid={`ps-row-${row.id}-rpm`}>{row.rpm === null ? '—' : fmt(row.rpm, 0)}</TableCell>
                <TableCell className="px-1.5 sm:px-2 py-2 text-right font-mono whitespace-nowrap" data-testid={`ps-row-${row.id}-torque`}>{row.torqueNm === null ? '—' : fmt(row.torqueNm, 2)}</TableCell>
                <TableCell className="px-1.5 sm:px-2 py-2 text-right font-mono whitespace-nowrap" data-testid={`ps-row-${row.id}-power`}>{fmt(row.powerKw, 2)}</TableCell>
                <TableCell className={`px-1.5 sm:px-2 py-2 text-[11px] sm:text-xs ${stateTone(row.state)}`} data-testid={`ps-row-${row.id}-state`}>
                  {r.mode === 'general' ? GENERAL_STATE[row.state] : STATE_LABELS[row.state] || row.state}
                </TableCell>
              </TableRow>
            ))}
            <TableRow data-testid="ps-row-pinion">
              <TableCell className="px-1.5 sm:px-2 py-2">
                <span className="font-semibold">Planet pinion</span>
                <span className="block text-[10px] text-muted-foreground">relative to carrier, Zp {r.gear.zp}</span>
              </TableCell>
              <TableCell className="px-1.5 sm:px-2 py-2 text-right font-mono" data-testid="ps-row-pinion-rpm">{fmt(r.pinion.relRpm, 0)}</TableCell>
              <TableCell className="px-1.5 sm:px-2 py-2 text-right font-mono">—</TableCell>
              <TableCell className="px-1.5 sm:px-2 py-2 text-right font-mono">—</TableCell>
              <TableCell className="px-1.5 sm:px-2 py-2 text-muted-foreground">abs. {fmt(r.pinion.absRpm, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div className="rounded-md bg-muted/50 p-3 space-y-1.5" data-testid="ps-balance">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Power balance</p>
          {r.mode === 'ths' ? (
            <>
              <BalanceLine ok={Math.abs(b.residualW) < 1e-6} testId="ps-balance-planetary">
                Engine {fmt(b.engineKw, 2)} kW = ring {fmt(b.ringKw, 2)} kW + MG1 {fmt(b.mg1TakenKw, 2)} kW + mesh loss {fmt(b.meshLossKw, 2)} kW
              </BalanceLine>
              <BalanceLine ok={Math.abs(b.overallResidualW) < 1e-6} testId="ps-balance-overall">
                Engine {fmt(b.engineKw, 2)} kW + battery {fmt(b.batteryKw, 2)} kW = wheels {fmt(b.wheelKw, 2)} kW + losses {fmt(b.meshLossKw + b.elecLossKw, 2)} kW
              </BalanceLine>
            </>
          ) : (
            <BalanceLine ok={Math.abs(b.residualW) < 1e-6} testId="ps-balance-planetary">
              Power in {fmt(b.inKw, 2)} kW = power out {fmt(b.outKw, 2)} kW + mesh loss {fmt(b.meshLossKw, 2)} kW
            </BalanceLine>
          )}
        </div>

        <div className="space-y-2" data-testid="ps-warnings">
          {warnings.length === 0 && infos.length === 0 && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="ps-no-warnings">
              <CheckCircle2 className="h-3.5 w-3.5" /> No warnings: within all limits set.
            </p>
          )}
          {warnings.map((w) => (
            <div key={w.code} className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-800 leading-relaxed" data-testid={`ps-warning-${w.code}`}>
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w.message}
            </div>
          ))}
          {infos.map((w) => (
            <div key={w.code} className="flex items-start gap-2 rounded-md bg-muted/60 p-2.5 text-xs text-muted-foreground leading-relaxed" data-testid={`ps-warning-${w.code}`}>
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w.message}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PowerSplitCalculator;
